import React, { useState } from "react";
import { CheckCircle2, ArrowRight, Cpu, Zap, ShieldCheck, Database, Layers, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const caseStudies = [
  {
    id: "logistics-voice",
    badge: "AI VOICE & TELECALLING",
    badgeColor: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    title: "Autonomous Inbound Voice SDR for Freight Logistics",
    clientIndustry: "Global Logistics & Freight Enterprise",
    metricBadge: "82% Calls Automated • <380ms Latency",
    architecture: [
      { name: "Inbound Carrier SIP", desc: "Twilio SIP Trunking" },
      { name: "WebRTC Audio Engine", desc: "Sub-200ms Jitter Buffer" },
      { name: "Realtime LLM Pipeline", desc: "OpenAI Realtime + Tool Call" },
      { name: "Enterprise ERP Sync", desc: "PostgreSQL & SAP API" },
    ],
    problem: "The enterprise handled 14,000+ daily inbound vendor inquiries regarding freight tracking, rate quotes, and dock appointments. Heavy call center queue times exceeded 25 minutes, causing significant customer churn.",
    solution: "We engineered a sovereign AI Voice Agent pipeline with WebRTC streaming and direct ERP database integration. Calls are answered instantly in under 380ms, authenticating callers via caller ID and executing real-time shipment lookups.",
    outcome: [
      "Reduced average caller wait time from 25 minutes to <0.5 seconds",
      "Automated 82% of routine tier-1 freight queries without human intervention",
      "Saved $42,000 monthly in offshore support operations",
      "Maintained 99.98% uptime with zero packet-loss failover",
    ],
  },
  {
    id: "ecommerce-scale",
    badge: "HIGH-THROUGHPUT WEB SYSTEMS",
    badgeColor: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    title: "Headless Decoupled Architecture for Omnichannel Retailer",
    clientIndustry: "Multi-Store E-Commerce & Retail",
    metricBadge: "$2.2M Extra Revenue • 0.8s LCP",
    architecture: [
      { name: "Global Edge DNS", desc: "Cloudflare Anycast Routing" },
      { name: "Headless Storefront", desc: "Next.js 14 SSG + Tailwind" },
      { name: "Redis Cache Layer", desc: "Multi-Region Inventory Cache" },
      { name: "Distributed Database", desc: "PostgreSQL Connection Pooling" },
    ],
    problem: "During peak promotional flash sales, the client's monolithic e-commerce application suffered 12+ second page load times and recurrent database lockouts, driving a 58% cart abandonment rate.",
    solution: "Our team rebuilt the frontend into a headless, static-optimized Next.js architecture with multi-region Redis caching. Catalog pages render in <100ms at the edge with dynamic cart hydration.",
    outcome: [
      "Cut Largest Contentful Paint (LCP) from 12.4s to 0.8s globally",
      "Maintained 100% availability across 150,000 concurrent peak shoppers",
      "Increased checkout conversion rate by 34%, generating $2.2M in additional ARR",
      "Reduced cloud hosting server compute costs by 45%",
    ],
  },
  {
    id: "healthtech-genomics",
    badge: "MOBILE HEALTH ARCHITECTURE",
    badgeColor: "bg-purple-500/10 text-purple-400 border-purple-500/30",
    title: "HIPAA-Compliant Diagnostics App with Biometric Vault",
    clientIndustry: "HealthTech & Genomics Diagnostics",
    metricBadge: "HIPAA Compliant • 99.4% Accuracy",
    architecture: [
      { name: "Native Mobile UI", desc: "Flutter 60 FPS Fluid" },
      { name: "Biometric Enclave", desc: "Hardware Secure Keystore" },
      { name: "Encrypted API Pipe", desc: "TLS 1.3 + mTLS Auth" },
      { name: "Isolated Cloud DB", desc: "HIPAA-Compliant AWS Vault" },
    ],
    problem: "Patients and lab physicians struggled with delayed genetic report deliveries and fragmented third-party lab portals that failed stringent patient data compliance audits.",
    solution: "Developed a cross-platform Flutter application featuring end-to-end encrypted biometric authentication, direct lab analyzer syncing, and automated diagnostic push alerts.",
    outcome: [
      "Reduced diagnostic turnaround report delivery time from 4 days to 4 hours",
      "Achieved 100% HIPAA and GDPR regulatory compliance verification",
      "Onboarded 75,000+ active patients with a 4.9-star App Store rating",
      "Zero security breaches or data leaks since production rollout",
    ],
  },
];

const CaseStudies = () => {
  return (
    <section className="py-24 bg-slate-50 dark:bg-[#07090e] border-t border-slate-200/80 dark:border-white/5 relative overflow-hidden" id="case-studies">
      {/* Background glow */}
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[140px] pointer-events-none" />

      <div className="wrapper relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
            <Layers size={14} /> Battle-Tested Case Studies
          </div>
          <h2 data-aos="fade-up" className="section-heading dark:text-white">
            Engineering Outcomes & Architectural Case Studies
          </h2>
          <p data-aos="fade-up" className="text-slate-600 dark:text-slate-400 text-base sm:text-lg">
            Explore how we engineer low-latency AI pipelines, modern distributed web apps, and enterprise platforms that drive millions in measurable business value.
          </p>
        </div>

        <div className="space-y-12">
          {caseStudies.map((study, index) => (
            <div
              key={study.id}
              data-aos="fade-up"
              data-aos-delay={index * 100}
              className="bg-white dark:bg-[#0c0f17] rounded-3xl p-6 sm:p-10 border border-slate-200 dark:border-white/10 shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden"
            >
              {/* Header Info */}
              <div className="flex flex-wrap items-start justify-between gap-4 pb-6 border-b border-slate-100 dark:border-white/5">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${study.badgeColor}`}>
                      {study.badge}
                    </span>
                    <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                      • {study.clientIndustry}
                    </span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                    {study.title}
                  </h3>
                </div>

                <div className="px-3.5 py-1.5 rounded-xl bg-emerald-500/10 text-emerald-500 dark:text-emerald-400 border border-emerald-500/20 text-xs sm:text-sm font-bold font-mono">
                  {study.metricBadge}
                </div>
              </div>

              {/* System Architecture Flow Diagram */}
              <div className="py-6 border-b border-slate-100 dark:border-white/5">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4 flex items-center gap-1.5">
                  <Cpu size={14} className="text-primary" /> Deployed System Architecture Flow:
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {study.architecture.map((node, nIdx) => (
                    <div
                      key={nIdx}
                      className="p-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5 relative flex flex-col justify-between"
                    >
                      <div className="text-xs font-bold text-slate-900 dark:text-white flex items-center justify-between">
                        <span>{node.name}</span>
                        <span className="text-[10px] font-mono text-primary">0{nIdx + 1}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                        {node.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Challenge vs Solution & Outcomes */}
              <div className="grid md:grid-cols-12 gap-8 pt-6">
                <div className="md:col-span-6 space-y-4">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-red-500 dark:text-red-400 mb-1">
                      The Challenge
                    </h4>
                    <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                      {study.problem}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-primary mb-1">
                      Our Architectural Solution
                    </h4>
                    <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                      {study.solution}
                    </p>
                  </div>
                </div>

                <div className="md:col-span-6 bg-slate-50 dark:bg-white/[0.02] p-5 sm:p-6 rounded-2xl border border-slate-200/60 dark:border-white/5 space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mb-2">
                    Verified Business & Technical Outcomes:
                  </h4>
                  <ul className="space-y-2.5">
                    {study.outcome.map((point, pIdx) => (
                      <li key={pIdx} className="flex items-start gap-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
                        <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 primary-btn px-8 py-4 rounded-2xl text-white font-semibold text-sm sm:text-base shadow-xl shadow-primary/20"
          >
            <span>Request a Custom Technical Architecture Review</span>
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
